import React, { useEffect, useState, useRef, useMemo } from 'react'
import Chat, { Message } from '../components/Chat'
import { toast } from 'react-toastify';

import { FaCircle, FaLink, FaPhoneAlt, FaTrash, FaWhatsapp } from "react-icons/fa";

import RecentChats, { RecentChat } from '../components/RecentChats';
import moment from 'moment';
import { API_BASE, apiFetch } from '../lib/api';
import { useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import type { MainLayoutOutletContext } from '../components/MainLayout';
import * as Utils from "../utils";
import { FaLocationPin } from 'react-icons/fa6';
import { MdWifiCalling } from 'react-icons/md';

type SearchableDistributorProps = {
    value: string
    onChange: (val: string) => void
}

type Concern = {
    id: number
    concern: string | null
    summary: string | null
    intent: string | null
    notes: string | null
    text: string | null
    created_at: string
}

function SearchableDistributor({ value, onChange }: SearchableDistributorProps) {
    const [search, setSearch] = useState<string>(value ?? '')
    const [focused, setFocused] = useState(false)
    const [results, setResults] = useState<string[]>([])

    useEffect(() => {
        setSearch(value ?? '')
    }, [value])

    // Debounced server-side search (matches mother_name / mother_code).
    useEffect(() => {
        if (!focused) return
        const query = search.trim()
        if (query.length < 2) {
            setResults([])
            return
        }
        const handle = setTimeout(async () => {
            try {
                const res = await apiFetch(`${API_BASE}/api/distributors?q=${encodeURIComponent(query)}`)
                const data = await res.json()
                if (data?.ok) setResults(data.distributors ?? [])
            } catch (err) {
                // noop
            }
        }, 300)
        return () => clearTimeout(handle)
    }, [search, focused])

    return (
        <div className='relative'>
            <input
                className="w-full border rounded p-2"
                placeholder='Type to search dealers...'
                value={search}
                onChange={(e) => { setSearch(e.target.value); setFocused(true) }}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
            />

            {focused && results.length > 0 && (
                <ul className="absolute z-20 w-full max-h-48 overflow-auto bg-white border rounded mt-1">
                    {results.map((d) => (
                        <li
                            key={d}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 mr-4"
                            onMouseDown={() => { onChange(d); setSearch(d); setFocused(false) }}
                        >
                            {Utils.formattedName(d)}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

const WS_BASE = API_BASE.replace(/^http/, 'ws');

export default function Demo() {
    const { authUser } = useOutletContext<MainLayoutOutletContext>();
    const userRole = authUser?.role ?? null;

    const location = useLocation()

    const ws = useRef<WebSocket | null>(null);
    const reconnectTimerRef = useRef<number | null>(null);
    const shouldReconnectRef = useRef(true);

    const [searchParams, setSearchParams] = useSearchParams();

    const [distributor, setDistributor] = useState<string>(() => searchParams.get('distributor') || '')
    const distributorRef = useRef(distributor)

    const [messages, setMessages] = useState<Message[]>([])
    const [showConcerns, setShowConcerns] = useState(false);
    const [concerns, setConcerns] = useState<Concern[]>([]);
    const [llmRunning, setLlmRunning] = useState(false)
    const [phone, setPhone] = useState(() => {
        const item = localStorage.getItem("phone");
        if (item) {
            return item;
        }

        return "";
    });

    const [distributorMeta, setDistributorMeta] = useState<{
        number: string | null,
        depot: string[],
        terr_name: string[],
        tsi_names: string[],
        category: string[],
        club_details: string[],
    }>(() => {
        return {
            number: null,
            depot: [],
            terr_name: [],
            tsi_names: [],
            category: [],
            club_details: [],
        }
    })

    const [communicationMode, _setCommunicationMode] = useState<'chat' | 'whatsapp'>(() => {
        return localStorage.getItem('communicationMode') as any || 'chat'
    });

    const [isWsConnected, setIsWsConnected] = useState(false)

    const [waConvoLimit, setWAConvoLimit] = useState(moment().subtract(1, 'month'))

    useEffect(() => {
        distributorRef.current = distributor
    }, [distributor])

    const toChatMessage = (message: any): Message => ({
        ...message,
        mode: message.mode ?? message.channel ?? 'webchat',
        agent: message.agent ?? null,
    })

    const upsertMessage = (message: Message) => {
        setMessages((prevMessages) => {
            const existingIndex = prevMessages.findIndex((m) => m.id === message.id)
            if (existingIndex === -1) return [...prevMessages, message]

            const nextMessages = [...prevMessages]
            nextMessages[existingIndex] = { ...nextMessages[existingIndex], ...message }
            return nextMessages
        })
    }

    useEffect(() => {
        const val = searchParams.get('distributor');

        if (!val) return;

        localStorage.setItem('last.distributor', val);

        const newParams = new URLSearchParams(searchParams);
        newParams.delete('distributor');

        setSearchParams(newParams);
    }, []);

    const setCommunicationMode = async (mode: 'chat' | 'whatsapp') => {
        if (communicationMode === mode) return;

        localStorage.setItem('communicationMode', mode)

        if (mode === 'whatsapp') {
            let notiMessage: Message

            if (waConvoLimit.isBefore(moment())) {
                toast.info('Whatsapp mode is enabled. Your messages will be directly relayed over WhatsApp to user')

                notiMessage = {
                    id: new Date().toISOString(),
                    sender: "system",
                    createdAt: new Date().toISOString(),
                    text: "To start free style whatsapp conversation, user must send initial message. You can start by sending a template message below",
                    mode: "system",
                    agent: ''
                }
            } else {
                notiMessage = {
                    id: new Date().toISOString(),
                    sender: "system",
                    createdAt: new Date().toISOString(),
                    text: "Whatsapp mode is enabled. Your messages will be directly relayed over WhatsApp to user",
                    mode: "system",
                    agent: ''
                }
            }

            const newMessages = [...messages, notiMessage]

            setMessages(newMessages)
        }

        _setCommunicationMode(mode)
    }

    const mapNumber = async (sendTemplate: boolean) => {
        if (!distributor) {
            toast.error("Please select a dealer")
            return;
        }

        try {
            const res = await apiFetch(`${API_BASE}/api/number/meta`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phone, distributor, sendTemplate }),
            });

            if (res.ok) {
                // setCommunicationMode('whatsapp')
                toast.info(`Temporarily associated ${phone} to ${Utils.formattedName(distributor)}`)
            }
        } catch (err) {
            // noop
            toast.error(`error mapping number: ${err}`)
        }
    }

    useEffect(() => {
        if (!phone || phone.length !== 10) {
            return
        }

        apiFetch(`${API_BASE}/api/whatsapp/validity?phone=${encodeURIComponent(phone)}`)
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    setWAConvoLimit(moment.utc(data.validity).local())
                }
            })
    }, [phone])

    const sendWATemplate = async () => {
        if (!distributor || !phone) {
            toast.error("Please enter dealer and phone number")
            return;
        }

        try {
            const res = await apiFetch(`${API_BASE}/api/whatsapp/message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: 'template',
                    distributor,
                    phone,
                }),
            });
            if (res.ok) {
                toast.info(`Sent message to dealer: ${distributor}`)
            }
        } catch (err) {
            // noop
        }
    }

    const createWS = () => {
        if (!shouldReconnectRef.current) {
            return
        }

        let query = ''
        if (distributor !== '') {
            query = `?distributor=${encodeURIComponent(distributor)}`
        }
        const wsUrl = `${WS_BASE}/api/ws${query}`;
        ws.current = new WebSocket(wsUrl);

        const clearTimer = () => {
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current)
                reconnectTimerRef.current = null
            }
        }

        ws.current.onopen = () => {
            console.log(`WebSocket connected: dealer: ${distributor}`);
            setIsWsConnected(true);
            clearTimer()

            if (distributor !== '') {
                handleDistributorChange(distributor)
            }
        };

        const handleWsMessage = (event: MessageEvent) => {
            const message = JSON.parse(event.data);
            console.log(`Received message from dealer: ${distributorRef.current}:`, message);

            switch (message.type) {
                case 'recent':
                    setRecentChats((recents) => {
                        // console.log('recent', message, recents)
                        const filtered = recents.filter(x => x.distributor !== message.distributor)
                        return [message, ...filtered]
                    })
                    if (message.distributor === distributorRef.current && message.sender === 'llm') {
                        upsertMessage(toChatMessage(message))
                        setLlmRunning(false)
                    }
                    break
                case 'wa.validity':
                    if (phone === message.phone) {
                        setWAConvoLimit(moment.utc(message.validity).local())
                    }
                    break
                case 'alert':
                    toast.info(message.message)
                    break
                case 'llmResponse':
                    upsertMessage(toChatMessage(message.data));
                    setLlmRunning(message.loading)
                    break;
                case 'userQuery':
                    upsertMessage(toChatMessage(message.data));
                    setLlmRunning(message.loading)
                    return;
                default:
                    console.warn(`Unknown message type: ${message.type}`);
                    break;
            }
        }

        ws.current.onmessage = async (event) => {
            if (typeof event.data === 'string') {
                handleWsMessage(event)
            }
        };

        ws.current.onclose = () => {
            console.log(`WebSocket disconnected dealer: ${distributor}`);
            setIsWsConnected(false);
            clearTimer()
            if (shouldReconnectRef.current) {
                reconnectTimerRef.current = setTimeout(createWS, 5000)
            }
        };
    }

    useEffect(() => {
        shouldReconnectRef.current = true

        if (!isWsConnected) {
            createWS()
        }

        return () => {
            shouldReconnectRef.current = false
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current)
                reconnectTimerRef.current = null
            }
            ws.current?.close();
        };
    }, []);

    useEffect(() => {
        let mounted = true
        const fetchMessages = async () => {
            if (!distributor) return
            try {
                const res = await apiFetch(`${API_BASE}/api/distributors/messages?distributor=${encodeURIComponent(distributor)}`)
                const data = await res.json()
                if (mounted && data?.ok) {
                    setMessages(data.messages as Message[])
                }
            } catch (err) {
                // noop
            }
        }

        const fetchNumberMapping = async () => {
            if (!distributor) return
            try {
                const res = await apiFetch(`${API_BASE}/api/distributor/phone?distributor=${encodeURIComponent(distributor)}`)
                const data = await res.json()
                if (mounted && data?.ok) {
                    setPhone(data.phone as string)
                }
            } catch (err) {
                // noop
            }
        }

        const fetchNumbers = async () => {
            if (!distributor) return
            try {
                const res = await apiFetch(`${API_BASE}/api/distributor/meta?distributor=${encodeURIComponent(distributor)}`)
                const data = await res.json()
                if (mounted && data?.ok) {
                    setDistributorMeta(data.meta)
                }
            } catch (err) {
                // noop
            }
        }

        fetchNumbers()
        fetchNumberMapping()
        fetchMessages()
        return () => { mounted = false }
    }, [distributor])

    const [recents, setRecentChats] = useState<RecentChat[]>([])
    // restore last dealer + load recents on mount
    useEffect(() => {
        const load = async () => {
            const lastDistributor = localStorage.getItem('last.distributor') || ''
            if (lastDistributor && distributor === "") {
                handleDistributorChange(lastDistributor)
            }

            try {
                const res = await apiFetch(`${API_BASE}/api/chat/recent`)
                const data = await res.json()
                if (data?.ok) {
                    setRecentChats(data.recent ?? [])
                }
            } catch (err) {
                // noop
            }
        }
        load()
    }, [])

    const handleFetchConcerns = async () => {
        if (!distributor) return;
        try {
            const res = await apiFetch(`${API_BASE}/api/concerns?distributor=${encodeURIComponent(distributor)}`);
            const data = await res.json();
            if (data.ok) {
                setConcerns(data.concerns);
                setShowConcerns(true);
            }
        } catch (err) {
            // noop
        }
    };

    const handleDeleteConcern = async (id: number) => {
        try {
            const res = await apiFetch(`${API_BASE}/api/concerns/${id}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                toast.info("Concern deleted");
                setConcerns(concerns.filter(c => c.id !== id));
            } else {
                toast.error("Failed to delete concern");
            }
        } catch (err) {
            toast.error("Failed to delete concern");
        }
    };

    const handleDeleteAllConcerns = async () => {
        try {
            const res = await apiFetch(`${API_BASE}/api/concerns?distributor=${encodeURIComponent(distributor)}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                toast.info("All concerns deleted");
                setConcerns([]);
            } else {
                toast.error("Failed to delete all concerns");
            }
        } catch (err) {
            toast.error("Failed to delete all concerns");
        }
    };

    const handleDistributorChange = async (distributor: string) => {
        console.log("newDealer", distributor)
        setDistributor(distributor);
        localStorage.setItem('last.distributor', distributor)

        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'distributorChange', distributor: distributor }));
        }
    };

    const handleClearRecent = async (distributor: string) => {
        try {
            const res = await apiFetch(`${API_BASE}/api/chat/recent?distributor=${encodeURIComponent(distributor)}`, {
                method: 'DELETE',
            });

            if (res.ok) {
                setRecentChats((recents) => {
                    return recents.filter(x => x.distributor !== distributor)
                })
            }
        } catch (err) {
            // noop
        }
    };

    const addChatMode = () => {
        if (userRole == 'agent') {
            return <></>
        }
        return (
            <div className="flex items-center gap-3">
                <label className="block text-sm font-medium text-gray-700">Mode: </label>

                <span
                    className={`text-sm ml-10 ${communicationMode === 'chat'
                        ? 'font-semibold text-blue-600'
                        : 'text-gray-500'
                        }`}
                >
                    AI Chat
                </span>

                <label className="relative inline-flex cursor-pointer items-center">
                    <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={communicationMode === 'whatsapp'}
                        onChange={() => {
                            const prev = communicationMode
                            const newType = prev === 'chat' ? 'whatsapp' : 'chat'

                            setCommunicationMode(newType)
                        }}
                    />

                    {/* Track */}
                    <div
                        className="h-6 w-11 rounded-full bg-gray-300 transition-colors duration-300 peer-checked:bg-green-500"
                    />
                    {/* Thumb */}
                    <div
                        className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-300 peer-checked:translate-x-5"
                    />
                </label>

                <span
                    className={`text-sm ${communicationMode === 'whatsapp'
                        ? 'font-semibold text-green-600'
                        : 'text-gray-500'
                        }`}
                >
                    WhatsApp
                </span>
            </div>
        )
    }

    const hasPhone = useMemo(() => {
        return !!distributorMeta.number
    }, [distributorMeta])

    return (
        <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
            <main className="flex-grow flex flex-col lg:flex-row p-4 gap-4 overflow-y-auto">
                {/* Left sidebar */}
                <div className="lg:w-1/4 flex flex-col gap-4">
                    <RecentChats recents={recents}
                        handleDistributorChange={handleDistributorChange}
                        handleClearRecent={handleClearRecent} />
                </div>

                {/* Main chat area */}
                <div className="lg:w-2/4 flex flex-col gap-4">
                    <Chat
                        distributor={distributor}
                        messages={messages}
                        loading={llmRunning}
                        communicationMode={communicationMode}
                        waConvoLimit={waConvoLimit}
                        sendWATemplate={sendWATemplate}
                        handleFetchConcerns={handleFetchConcerns}
                        onSend={async (text) => {
                            if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
                                toast.info("Agent is not connected")
                                return
                            }

                            if (!distributor) {
                                toast.error("Please select a dealer")
                                return
                            }

                            if (communicationMode === 'whatsapp') {
                                if (!phone) {
                                    toast.error("Please enter phone number")
                                    return
                                }

                                ws.current.send(JSON.stringify({ type: 'waReply', distributor, text, phone }));
                            } else {
                                const clientRequestId = `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6)}`
                                upsertMessage({
                                    id: clientRequestId,
                                    distributor,
                                    sender: 'user',
                                    text,
                                    createdAt: new Date().toISOString(),
                                    mode: 'webchat',
                                    agent: authUser?.name ?? null,
                                } as Message)
                                setLlmRunning(true)
                                ws.current.send(JSON.stringify({ type: 'llmRequest', distributor, text, clientRequestId }));
                            }
                        }}
                    />
                </div>
                {/* Sidebar for controls */}
                <div className="lg:w-1/4 flex min-h-0 flex-col gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-md flex flex-1 min-h-0 flex-col gap-2 relative">
                        <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
                            Actions
                            {isWsConnected ? (
                                <FaCircle className="text-green-500 text-xs"
                                    data-tooltip-id="my-tooltip"
                                    data-tooltip-content="Connected" />
                            ) : (
                                <span className="text-sm font-normal text-red-500">(Offline, reconnecting...)</span>
                            )}
                        </h3>
                        <div className="space-y-3">
                            {/* choose dealer dropdown option */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Choose Dealer</label>
                                <SearchableDistributor
                                    value={distributor}
                                    onChange={handleDistributorChange}
                                />
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                            {/* dealer meta info */}
                            <p className='text-sm font-medium text-gray-700 py-2'>Dealer Details</p>
                            <div className='flex flex-col items-start space-y-3'>

                                {/* <label className="block text-sm font-medium text-gray-700">{Utils.formattedName(distributor)}</label> */}
                                {distributorMeta.number && (
                                    <div className='flex flex-row flex-wrap gap-2 items-center'>
                                        <FaPhoneAlt className=' text-gray-600 text-xs' />
                                        <a className='text-sm text-gray-600 cursor-pointer underline hover:text-blue-600' href={`tel:${distributorMeta.number}`}>{distributorMeta.number}</a>
                                    </div>
                                )}

                                <ul className='list-disc list-inside'>
                                    {distributorMeta.depot?.length > 0 && (
                                        <li className="capitalize text-sm text-gray-600">Depot: {distributorMeta.depot.join(', ')}</li>
                                    )}

                                    {distributorMeta.terr_name?.length > 0 && (
                                        <li className="capitalize text-sm text-gray-600">Terr Name: {distributorMeta.terr_name.join(', ')}</li>
                                    )}

                                    {distributorMeta.category?.length > 0 && (
                                        <li className="capitalize text-sm text-gray-600">Category: {distributorMeta.category.length > 1 ? "Multiple" : distributorMeta.category.join(', ')}</li>
                                    )}

                                    {distributorMeta.club_details?.length > 0 && (
                                        <li className="capitalize text-sm text-gray-600 capitalize">Club Details: {distributorMeta.club_details.join(', ').toLocaleLowerCase()}</li>
                                    )}
                                </ul>

                            </div>

                            <div className="border-t border-gray-300 w-full mt-2"></div>

                            {userRole !== 'agent' && (
                                <div className="space-y-3 mt-2">
                                    <label className="block text-md font-medium text-gray-700">Advanced</label>
                                    {/* phone */}
                                    <div className="flex items-center gap-2">
                                        <label htmlFor="language-select" className="text-sm font-medium text-gray-700 whitespace-nowrap w-60">Phone</label>
                                        <input
                                            type="phone"
                                            value={phone}
                                            onChange={(e) => {
                                                localStorage.setItem("phone", e.target.value?.trim() || '')
                                                setPhone(e.target.value)
                                            }}
                                            placeholder="Enter phone number"
                                            className="w-full px-3 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />

                                        {/* <button
                                            onClick={mapNumber}
                                            disabled={!distributor}
                                            data-tooltip-id="my-tooltip"
                                            data-tooltip-content="Temporary Link Phone to dealer to test associate incoming whatsapp message for this dealer"
                                            className="w-14 h-10 border border-transparent text-sm font-medium rounded-md
                 text-white bg-lime-600 hover:bg-lime-700
                 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lime-500
                 disabled:bg-lime-300 disabled:cursor-not-allowed cursor-pointer
                 flex items-center justify-center"
                                        >
                                            <FaLink />
                                        </button> */}
                                    </div>

                                    {/* action options */}
                                </div>
                            )}

                        </div>

                        {/* {userRole !== 'agent' && (
                            <span className="flex items-center"
                                data-tooltip-id="my-tooltip"
                                data-tooltip-content="Talk to AI agent over whatsapp by messaging number"
                            >
                                <FaWhatsapp className="mr-2 text-green-500" /> Whatsapp Bot:&nbsp;<a href="https://wa.me/15557123434?text=Hello" className='underline text-blue-600 text-sm' target='__blank'> +15557123434</a></span>
                        )} */}
                    </div>
                </div>

            </main>
            {/* <Footer /> */}

            {/* Concerns Modal */}
            {showConcerns && (
                <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full flex items-center justify-center z-30">
                    <div className="bg-white p-5 rounded-lg shadow-xl w-full max-w-2xl mx-4">
                        <div className="flex justify-between items-center mb-4 px-3 pt-2">
                            <h2 className="text-xl font-bold">Concerns: {Utils.formattedName(distributor)} ({concerns.length})</h2>
                            <button
                                onClick={() => setShowConcerns(false)}
                                className="h-8 w-8 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 cursor-pointer text-sm font-semibold"
                                aria-label="Close concerns"
                            >
                                X
                            </button>

                            {/* <button
                                onClick={handleDeleteAllConcerns}
                                className="px-4 py-2 bg-red-600 text-white rounded cursor-pointer hover:bg-red-700 disabled:bg-red-300 text-sm"
                                disabled={concerns.length === 0}
                            >
                                Clear
                            </button> */}
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {concerns.length > 0 ? (
                                <ul className="divide-y divide-gray-200">
                                    {concerns.map((c) => (
                                        <li key={c.id} className="p-3 flex justify-between items-start">
                                            <div>
                                                {c.intent && <p className="text-sm text-gray-800"><strong>Intent:</strong> {c.intent}</p>}
                                                {c.notes && <p className="text-sm text-gray-800 mt-0.5"><strong>Notes:</strong> {c.notes}</p>}
                                                {c.concern && <p className="text-sm text-gray-800"><strong>Concern:</strong> {c.concern}</p>}
                                                {c.summary && <p className="text-sm text-gray-800 mt-0.5"><strong>Summary:</strong> {c.summary}</p>}
                                                {c.text && <p className="text-sm text-gray-600 mt-1"><strong>Dealer Said:</strong> {c.text}</p>}
                                                <p className="text-xs text-gray-500 mt-2"><strong>Date:</strong> {new Date(c.created_at).toLocaleString()}</p>
                                            </div>
                                            <button
                                                data-tooltip-content={"Remove concern"}
                                                data-tooltip-id='my-tooltip'
                                                onClick={() => handleDeleteConcern(c.id)} className="text-gray-500 hover:text-red-500 ml-4 p-2 cursor-pointer">
                                                <FaTrash />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>No concerns found for this dealer.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}
